import { useState, useCallback } from 'react'
import { MessageSquare } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface NotePopoverProps {
  note: string | null
  onSave: (note: string | null) => void
  disabled?: boolean | undefined
}

export function NotePopover({ note, onSave, disabled }: NotePopoverProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(note ?? '')

  const hasNote = !!note?.trim()

  const handleOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setDraft(note ?? '')
      }
      setOpen(isOpen)
    },
    [note]
  )

  const handleSave = useCallback(() => {
    const trimmed = draft.trim()
    onSave(trimmed || null)
    setOpen(false)
  }, [draft, onSave])

  return (
    <>
      <Popover open={open} onOpenChange={handleOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded-lg transition-colors',
              'hover:bg-accent/50 disabled:opacity-50 disabled:pointer-events-none',
              hasNote
                ? 'text-foreground'
                : 'text-subtle hover:text-muted-foreground'
            )}
            title={hasNote ? note! : 'Add note'}
          >
            <MessageSquare className={cn('h-4 w-4', hasNote && 'fill-current')} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start" side="bottom">
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
            Note
          </label>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a supporting comment..."
            className="min-h-[72px] text-sm resize-none"
            autoFocus
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {hasNote && (
        <p
          className={cn(
            'hidden [tr[data-expanded]_&]:block',
            'mt-1 text-meta whitespace-pre-wrap break-words'
          )}
        >
          {note}
        </p>
      )}
    </>
  )
}
